import type { LocaleData } from '@/types/index'
import { type ViewMetadata } from '@/types/index'
import { useRef, useState, type JSX } from 'react'
import { format } from 'date-fns'
import { useRegistry } from '@/hooks/useRegistry'
import { handleLink } from '@/components/Link/lib/handleLink'
import { useHistory, useNavigation, useView } from '@/hooks/index'
import { cn } from '@ttab/elephant-ui/utils'
import { DotMenu } from '@/components/ui/DotMenu'
import { Link } from '@/components'
import { PenIcon, CalendarDaysIcon } from '@ttab/elephant-ui/icons'
import type { HitV1 } from '@ttab/elephant-api/index'
import { useDeliverableInfo } from '@/hooks/useDeliverableInfo'
import { useLatest } from './hooks/useLatest'
import { SluglineButton } from '@/components/DataItem/Slugline'
import { SectionBadge } from '@/components/DataItem/SectionBadge'
import { useTranslation } from 'react-i18next'
import { CreatePrintArticle } from '@/components/CreatePrintArticle'
import { useModal } from '@/components/Modal/useModal'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { documentTypeValueFormat } from '@/defaults/documentTypeFormats'

const meta: ViewMetadata = {
  name: 'Latest',
  path: `${import.meta.env.BASE_URL || ''}/latest`,
  widths: {
    sm: 12,
    md: 12,
    lg: 6,
    xl: 6,
    '2xl': 6,
    hd: 6,
    fhd: 4,
    qhd: 3,
    uhd: 2
  }
}

export const Latest = ({ setOpen }: { setOpen?: (open: boolean) => void }) => {
  const { locale } = useRegistry()
  const data = useLatest()
  const { t } = useTranslation('common')

  if (!data?.length) {
    return <div className='min-h-screen text-center py-2'>{t('misc.loading')}</div>
  }

  return (
    <Content documents={data} locale={locale} setOpen={setOpen} />
  )
}

const Content = ({ documents, locale }: {
  documents: HitV1[]
  locale: LocaleData
  setOpen?: (open: boolean) => void
}): JSX.Element => {
  const { state, dispatch } = useNavigation()
  const history = useHistory()
  const { viewId } = useView()

  const origin = useRef(history.state?.viewId)

  function getLocalizedDate(date: Date, locale: LocaleData): string | undefined {
    if (!locale.module) {
      console.warn(`Locale ${locale.code.full} not supported.`)
      return format(date, 'dd MMM yyyy – HH.mm')
    }

    return format(date, 'dd MMM yyyy – HH.mm', { locale: locale.module })
  }

  return (
    <div className='min-h-screen max-h-screen overflow-y-auto divide-y'>
      {documents.map((item: HitV1) => {
        if (!item) {
          return <></>
        }

        const id = item.id
        const title = item.fields['document.title']?.values[0]
        const lastUsableVersion = item.fields['heads.usable.version']?.values[0]
        const publish = item.fields['heads.usable.created']?.values[0]
        const slugline = item.fields['document.meta.tt_slugline.value']?.values[0]
        const section = item.fields['document.rel.section.title']?.values[0]
        const uri = item.fields['document.uri']?.values[0]

        return (
          <div
            key={id}
            className={cn('hover:bg-table-focused dark:hover:bg-table-focused flex flex-row py-3 pl-5 pr-3',
              id ? 'cursor-pointer' : 'cursor-not-allowed'
            )}
            onClick={() => {
              if (!id) {
                return
              }

              handleLink({
                dispatch,
                viewItem: state.viewRegistry.get('Editor'),
                props: { id, version: lastUsableVersion?.toString() },
                viewId: crypto.randomUUID(),
                history,
                origin: origin.current || viewId
              })
            }}
          >
            <div className='grow'>
              <div className='text-sm flex flex-col gap-2'>
                <div className='font-medium'>{title}</div>

                <div className='text-xs text-muted-foreground'>
                  {publish && <div>{`${getLocalizedDate(new Date(publish), locale)}`}</div>}
                </div>

                <div className='flex gap-2 items-center w-full text-muted-foreground  -ml-1'>
                  <SluglineButton value={uri.startsWith('core://flash') ? 'TT-FLASH' : slugline} />
                  <SectionBadge title={section} />
                </div>
              </div>
            </div>
            <Menu articleId={id} />
          </div>
        )
      }
      )}
    </div>
  )
}

const Menu = ({ articleId }: { articleId: string }): JSX.Element => {
  // Defer the deliverable-info fetch until the menu is opened (per-row lazy
  // mount avoids fan-out fetch storms when the list contains many items).
  const [hasOpened, setHasOpened] = useState(false)
  const planningId = useDeliverableInfo(hasOpened ? articleId : '')?.planningUuid ?? ''
  const { t } = useTranslation('common')
  const { showModal, hideModal } = useModal()
  const featureFlags = useFeatureFlags(['hasPrint'])
  const printIcon = documentTypeValueFormat['tt/print-article'].icon

  return (
    <div className='shrink p-'>
      <DotMenu
        onOpenChange={(open) => {
          if (open) {
            setHasOpened(true)
          }
        }}
        items={[
          {
            label: t('actions.openType', { type: t('core:documentType.article') }),
            item: (
              <Link to='Editor' target='last' props={{ id: articleId }} className='flex flex-row gap-5'>
                <div className='pt-1'>
                  <PenIcon size={14} strokeWidth={1.5} className='shrink' />
                </div>
                <div className='grow'>{t('actions.openType', { type: t('core:documentType.article') })}</div>
              </Link>
            )
          },
          {
            label: t('actions.openType', { type: t('core:documentType.planning') }),
            disabled: !planningId,
            item: planningId
              ? (
                  <Link to='Planning' target='last' props={{ id: planningId }} className='flex flex-row gap-5'>
                    <div className='pt-1'>
                      <CalendarDaysIcon size={14} strokeWidth={1.5} className='shrink' />
                    </div>
                    <div className='grow'>{t('actions.openType', { type: t('core:documentType.planning') })}</div>
                  </Link>
                )
              : () => {}
          },
          ...(featureFlags.hasPrint
            ? [{
                label: t('planning:assignment.createPrintArticle'),
                icon: printIcon,
                item: () => {
                  showModal(
                    <CreatePrintArticle
                      id={articleId}
                      asDialog
                      onDialogClose={hideModal}
                    />
                  )
                }
              }]
            : [])
        ]}
      />
    </div>
  )
}

Latest.meta = meta
